import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import ArticleCard from "@/components/ArticleCard";
import { SEO } from "@/components/SEO";
import api from "@/lib/api";

export default function BlogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rubriques, setRubriques] = useState([]);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const page = parseInt(searchParams.get("page") || "1");
  const category = searchParams.get("categorie") || "";
  const rubrique = searchParams.get("rubrique") || "";

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 9 };
    if (category) params.category = category;
    if (rubrique) params.rubrique = rubrique;
    // Exclude Lives, Videos, Quiz from Publications (they belong in Médiathèque)
    if (!rubrique) params.exclude_rubriques = "61b18e64-ce6f-48fa-a568-85b65fffebab,e282b739-71bf-494c-a6d9-b8ddd28d40f3,23ad1300-9ea9-42fe-908d-8bb686b61b89";
    Promise.all([
      api.get("/articles", { params }),
      api.get("/categories"),
      api.get("/rubriques"),
    ])
      .then(([artRes, catRes, rubRes]) => {
        setArticles(artRes.data.articles);
        setPages(artRes.data.pages);
        setCategories(catRes.data);
        setRubriques(rubRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [page, category, rubrique]);

  const setFilter = (key, value) => {
    const params = {};
    if (key === "rubrique" && value) params.rubrique = value;
    if (key === "categorie" && value) params.categorie = value;
    setSearchParams(params);
  };

  // Rubriques that belong in Médiathèque, not Publications
  const MEDIA_RUBRIQUE_SLUGS = ["lives-dofa", "videos", "quiz"];
  const publicationRubriques = rubriques.filter((r) => !MEDIA_RUBRIQUE_SLUGS.includes(r.slug));

  return (
    <>
      <SEO title="Nos Publications" description="Tous les articles, analyses et ressources éditoriales d'OFALycée sur l'éducation bilingue et internationale." url="/blog" />

      {/* Hero Banner */}
      <section className="bg-[#14274B] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-sm font-mono tracking-widest uppercase text-white/50 mb-4 block">Nos Publications</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6" data-testid="blog-page-title">Tous les articles</h1>
          <p className="text-lg text-white/60 max-w-2xl leading-relaxed">
            Analyses, interviews et ressources éditoriales sur l'éducation bilingue et internationale.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="blog-page">

        {/* Rubrique tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="rubrique-filters">
          <Button
            variant={!rubrique ? "default" : "outline"}
            className="rounded-none text-sm"
            onClick={() => setFilter("rubrique", "")}
            data-testid="rubrique-all"
          >
            Tout
          </Button>
          {publicationRubriques.map((rub) =>
            rub.external_link ? (
              <a key={rub.id} href={rub.external_link} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-none text-sm" data-testid={`rubrique-${rub.slug}`}>
                  {rub.name} <span className="ml-1 text-[10px]">↗</span>
                </Button>
              </a>
            ) : (
              <Button
                key={rub.id}
                variant={rubrique === rub.id ? "default" : "outline"}
                className="rounded-none text-sm"
                onClick={() => setFilter("rubrique", rub.id)}
                data-testid={`rubrique-${rub.slug}`}
              >
                {rub.name} {rub.article_count > 0 && <span className="ml-1 text-xs opacity-70">({rub.article_count})</span>}
              </Button>
            )
          )}
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="category-filters">
          <span className="text-xs text-muted-foreground self-center mr-1">Thèmes :</span>
          <Button
            variant={!category ? "secondary" : "ghost"}
            size="sm"
            className="rounded-none text-xs h-7"
            onClick={() => setFilter("categorie", "")}
            data-testid="filter-all"
          >
            Tous
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={category === cat.id ? "secondary" : "ghost"}
              size="sm"
              className="rounded-none text-xs h-7"
              onClick={() => setFilter("categorie", cat.id)}
              data-testid={`filter-${cat.slug}`}
            >
              {cat.name} ({cat.article_count})
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse space-y-4">
                <div className="h-48 bg-muted" />
                <div className="h-4 bg-muted w-3/4" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">Aucun article trouvé.</p>
            <Button variant="outline" className="rounded-none mt-4" onClick={() => setSearchParams({})}>
              Voir tous les articles
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}
        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-16" data-testid="pagination">
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                className="rounded-none w-10 h-10"
                onClick={() => {
                  const params = { page: p.toString() };
                  if (category) params.categorie = category;
                  if (rubrique) params.rubrique = rubrique;
                  setSearchParams(params);
                }}
                data-testid={`page-${p}`}
              >
                {p}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
